import React, { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/modules/boost';                       // Boost auto-initialises (v12+)

import {
  ChartCompetitor,
  TimeseriesDataPoint,
} from '../types';
import {
  CompetitorColors,
  generateCompetitorColors,
} from '../utils/generators';
import { sortMetricsByDate } from '../utils/sorters';
import { getPerformanceBaseChartConfig } from '../utils/config';
import { DeviceType } from '@/features/property/hooks/useScrapedPropertyWithFilters';

/* ---------- props ---------- */

interface PriceChartProps {
  propertyDatapoints: TimeseriesDataPoint[];
  competitors: ChartCompetitor[];
  currencySymbol: string;
  deviceType: DeviceType;
}

/* ---------- component ---------- */

const PriceChart: React.FC<PriceChartProps> = ({
  propertyDatapoints,
  competitors,
  currencySymbol,
  deviceType,
}) => {
  const competitorColors = useMemo(
    () => generateCompetitorColors(competitors),
    [competitors],
  );

  const options = useMemo(
    () =>
      createPriceChartOptions(
        propertyDatapoints,
        competitors,
        currencySymbol,
        competitorColors,
        deviceType,
      ),
    [
      propertyDatapoints,
      competitors,
      currencySymbol,
      competitorColors,
      deviceType,
    ],
  );

  return (
    <div className="flex-1 min-w-[750px]">
      {Object.keys(options).length ? (
        <HighchartsReact highcharts={Highcharts} options={options} />
      ) : (
        <div className="bg-muted rounded-md flex items-center justify-center">
          <p className="text-muted-foreground">No price data available</p>
        </div>
      )}
    </div>
  );
};

/* ---------- helpers ---------- */

// Reverse the discount to get desktop price from mobile price
const reverseDiscount = (p: number, d = 0) => (d > 0 ? p / (1 - d / 100) : p);
/* ---------- chart builder ---------- */

const createPriceChartOptions = (
  propertyDataPoints: TimeseriesDataPoint[],
  competitors: ChartCompetitor[],
  currencySymbol: string,
  competitorColors: CompetitorColors,
  deviceType: DeviceType,
): Highcharts.Options => {
  if (!propertyDataPoints?.length) return {} as Highcharts.Options; // TS cast

  const sorted = sortMetricsByDate(propertyDataPoints);

  const OUR_COLOR = '#F59E0B';
  const MEDIAN_COLOR = '#1E293B';
  const base = getPerformanceBaseChartConfig();

  /* our price series (keeps point objects for colouring) */
  const ourPriceData = sorted.map(m => {
    const price =
      deviceType === 'desktop' && m.mobile_rate_discount
        ? reverseDiscount(m.price.our, m.mobile_rate_discount)
        : m.price.our;

    return [Date.parse(m.date), price];
  });

  /* build competitor lookup for tooltip */
  const competitorLookup: Record<string, Map<number, number>> = {};
  competitors.forEach(comp => {
    const map = new Map<number, number>();
    comp.data_points.forEach(p => {
      const value =
        deviceType === 'desktop' && p.mobile_rate_discount
          ? reverseDiscount(p.price, p.mobile_rate_discount)
          : p.price;
      map.set(Date.parse(p.date), value);
    });
    competitorLookup[comp.name] = map;
  });

  /* competitor lines – Perf tweaks */
  const competitorSeries: Highcharts.SeriesLineOptions[] = competitors.map(
    comp => ({
      name: comp.name,
      type: 'line',
      color: competitorColors[comp.property_id],
      data: sorted
        .map(m => competitorLookup[comp.name].get(Date.parse(m.date)))
        .map((y, i) => (y == null ? null : [Date.parse(sorted[i].date), y]))
        .filter(Boolean) as [number, number][],
      lineWidth: 1,
      opacity: 0.7,
      marker: { enabled: false },
      boostThreshold: 1,          // always WebGL
      enableMouseTracking: false, // KD-tree off
    }),
  );

  /* ---------- Highcharts options ---------- */

  return {
    ...base,
    chart: { ...base.chart },
    title: {
      text: 'Total Price (ADR x Length of Stay)',
      style: { fontWeight: 'bold' },
    },
    plotOptions: {
      ...base.plotOptions,
      series: {
        ...(base.plotOptions as any)?.series,
        boostThreshold: 1,
      },
      line: {
        ...((base.plotOptions as any)?.line || {}),
        pointPlacement: 'on',
        lineWidth: 3,
      },
    },
    tooltip: {
      shared: true,
      crosshairs: true,
      formatter: function () {
        // @ts-ignore
        const date = this.x as number;
        let html = `<b>${Highcharts.dateFormat('%b %d, %Y', date)}</b><br/>`;

        // @ts-ignore
        const pts = this.points as Highcharts.TooltipFormatterContextObject[];

        const our = pts.find(p => p.series.name === 'Our Price');
        const median = pts.find(
          p => p.series.name === 'Competitor Median Price',
        );

        if (our) {
          html += `<span style="color:${our.color}">●</span> <b>${our.series.name}:</b> <b>${currencySymbol}${Math.round(
            our.y as number,
          )}</b><br/>`;
        }
        if (median) {
          html += `<span style="color:${median.color}">●</span> <b>${median.series.name}:</b> <b>${currencySymbol}${Math.round(
            median.y as number,
          )}</b><br/>`;
        }

        if (competitors.length) {
          html += '<hr style="margin:4px 0"/>';
          competitors.forEach(comp => {
            const price = competitorLookup[comp.name].get(date);
            if (price != null) {
              html += `<span style="color:${competitorColors[comp.property_id]}">●</span> ${comp.name}: ${currencySymbol}${Math.round(
                price,
              )}<br/>`;
            }
          });
        }
        return html;
      },
    },
    series: [
      {
        name: 'Competitor Median Price',
        type: 'line',
        data: sorted.map(m => [Date.parse(m.date), m.price.median]),
        color: MEDIAN_COLOR,
        lineWidth: 3,
        connectNulls: true,
        boostThreshold: 1,
      },
      {
        name: 'Our Price',
        type: 'line',
        data: ourPriceData,
        color: OUR_COLOR,
        lineWidth: 4,
        marker: { radius: 5 },
        connectNulls: true,
        boostThreshold: 1,
      },
      ...competitorSeries,
    ],
  } as Highcharts.Options; // single cast silences TS
};

export default PriceChart;